import Joi, { ObjectSchema } from 'joi';
import { TFunction } from 'i18next';
import { Moment } from 'moment';
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from 'postcode-validator';

import type { IApplicationFormState } from 'pages/public/JobAdApplyForm/JobAdApplyForm.interface';
import type { TLocale } from 'interfaces/Locale.interface';
import type { ICustomFields } from 'interfaces/CustomFields.interface';
import { validationFieldRequired } from 'config/validationStrategy/validationFieldRequired';
import { phoneValidationStrategy } from 'config/validationStrategy/phoneValidationStrategy';
import { customFieldsService } from 'services/customFields/customFields.service';

const validatePostcode = (
  value: string,
  helpers: Joi.CustomHelpers<IApplicationFormState>
) => {
  if (!value || value === '') {
    return helpers.error('any.required');
  }

  const countryCode = helpers.state.ancestors[0].addressCountry;

  if (!countryCode || !postcodeValidatorExistsForCountry(countryCode)) {
    return value;
  }

  if (!postcodeValidator(value, countryCode)) {
    return helpers.error('validation.postcode');
  }

  return value;
};

const validateBirthDate = (
  value: Moment | null,
  helpers: Joi.CustomHelpers<unknown>,
  minAge?: number
) => {
  if (!value) {
    return helpers.error('date.required');
  }

  const isValidMinAge =
    Number(value) <=
    Number(new Date().setFullYear(new Date().getFullYear() - (minAge || 0)));

  const isValidAge = Number(value) < Number(new Date());

  if (minAge) {
    if (!isValidMinAge) {
      return helpers.error('validation.minAge');
    }
  }

  if (!isValidAge) {
    return helpers.error('validation.dateOfBirth');
  }

  return value;
};

export const jobAdSchema = (
  t: TFunction,
  language: TLocale,
  customFields?: ICustomFields,
  fileValidationStrategy?: (required: boolean) => Joi.AnySchema<unknown>,
  imageMimeTypeSchema?: Joi.AnySchema<unknown>,
  minAge?: number
): ObjectSchema<IApplicationFormState> => {
  return Joi.object({
    gender: Joi.string().required().messages(validationFieldRequired(t)),
    firstName: Joi.string().required().messages(validationFieldRequired(t)),
    lastName: Joi.string().required().messages(validationFieldRequired(t)),
    phone: phoneValidationStrategy.required().messages({
      ...validationFieldRequired(t),
      'any.invalid': t('errors:validation.phoneNumber'),
    }),
    email: Joi.string()
      .required()
      .email({ tlds: false })
      .messages({
        ...validationFieldRequired(t),
        'string.email': t('form:validation.email'),
      }),
    confirmEmail: Joi.string()
      .equal(Joi.ref('email'))
      .required()
      .messages({
        ...validationFieldRequired(t),
        'any.only': t('form:validation.emailMismatch'),
      }),
    addressCity: Joi.string().required().messages(validationFieldRequired(t)),
    addressStreet: Joi.string().required().messages(validationFieldRequired(t)),
    // todo: add custom country specific validation for zip code
    addressZip: Joi.custom((value, helpers) =>
      validatePostcode(value, helpers)
    ).messages({
      ...validationFieldRequired(t),
      'validation.postcode': t('form:validation.zipCode'),
    }),
    addressCountry: Joi.string()
      .required()
      .messages(validationFieldRequired(t)),
    dateOfBirth: Joi.required()
      .custom((value, helpers) => validateBirthDate(value, helpers, minAge))
      .messages({
        'any.required': t('modal.validation.fieldRequired'),
        'date.required': t('modal.validation.fieldRequired'),
        'validation.minAge': t('form:validation.minAge', { minAge }),
      }),
    acceptedRegulations: Joi.boolean()
      .required()
      .valid(true)
      .messages({
        'any.invalid': t('termsAndConditions.error'),
        'any.only': t('termsAndConditions.error'),
      }),
    avatar: imageMimeTypeSchema ? imageMimeTypeSchema : Joi.any(),
    ...customFieldsService({
      customFields,
      language,
    }).mapFieldsToJoiSchema(t, fileValidationStrategy, true),
  });
};
